/** Reichweitenmessung - tells the server that a page was looked at.
 *
 *  Sets no cookie, reads no storage and contacts nobody but our own server,
 *  which is what keeps the site free of a consent banner. The counterpart is
 *  backend/app/services/analytics.py; the „Reichweitenmessung" section of the
 *  Datenschutzerklärung describes both.
 */

import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { localeFromPath } from './i18n'

/** document.referrer belongs to the document, so it does not change as the
 *  visitor moves around inside the SPA. Sending it every time would credit
 *  Google with every page of a visit that merely started there. */
let documentIsFresh = true

function send(path: string, locale: string): void {
  const body = JSON.stringify({
    path,
    locale,
    referrer: documentIsFresh ? document.referrer : '',
  })
  documentIsFresh = false

  try {
    // sendBeacon survives the page being closed mid-request. It must be given
    // a Blob with an explicit type - a bare string is sent as text/plain and
    // the server would reject it.
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' })
      if (navigator.sendBeacon('/api/track', blob)) return
    }
    void fetch('/api/track', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => undefined)
  } catch {
    // Counting is never worth an error in a visitor's browser.
  }
}

/** Report the current route, once per navigation. Public pages only. */
export function usePageView(): void {
  const { pathname } = useLocation()
  // StrictMode mounts effects twice in development; without this the first
  // page of every dev session would be counted two times.
  const lastSent = useRef<string | null>(null)

  useEffect(() => {
    // Keeps Playwright and the deploy smoke test out of the real numbers.
    if (navigator.webdriver) return
    if (lastSent.current === pathname) return
    lastSent.current = pathname
    send(pathname, localeFromPath(pathname))
  }, [pathname])
}
